import { useCallback, useEffect, useState } from 'react';
import { useRouter } from 'next/compat/router';
import { useDispatch } from 'react-redux';
import { useDidUpdate } from 'rooks';
import styled from 'styled-components';

import { useIntl } from '@pass-frontend/common/i18n';
import { Geolocation } from '@pass-frontend/data-access/common/edge';
import { eligibilityLookupSlice } from '@pass-frontend/features/facility-checkin/data-access';
import { useCurrenEligibilityCheck } from '@pass-frontend/features/facility-checkin/data-bridge';
import { useLocationTypeahead } from '@pass-frontend/features/location-typeahead/data-bridge';
import {
  FormattedGeolocationForDropdown,
  LocationTypeahead,
} from '@pass-frontend/features/location-typeahead/ui';
import { useGymsSearchByLocation } from '@pass-frontend/features/standalone/data-bridge';
import { RdsButton } from '@pass-frontend/rally-energon-react';

import { ErrorInfo } from '../error-info';

const DEFAULT_LATITUDE: number | null = 37;
const DEFAULT_LONGITUDE: number | null = -92;
const DEFAULT_DISTANCE = 10;

const PartnerButton = styled(RdsButton)`
  min-width: 93px;
  &.rds-primary-button[disabled] {
    background-color: #cccccc;
    pointer-events: none;
    border-color: #cccccc;
  }
`;

const StyleAddressZipInput = styled.div`
  box-sizing: border-box;
  border: 1px solid #4d4d4d;

  .typeahead__control {
    height: 40px;
    font-size: 14px;
    box-sizing: border-box;
    border: 1px solid #b3b3b3;

    .typeahead__value-container {
      padding: 0 16px;
      cursor: text;
    }

    .typeahead__single-value {
      font-size: 14.22px;
      color: #000000;
      max-width: calc(100% - 32px);
    }

    .typeahead__placeholder {
      display: none;
    }
  }
`;

interface LocationSearchProps {
  defaultLocationOption?: FormattedGeolocationForDropdown;
  defaultLocationList?: { locationSuggestions: Geolocation[] };
  distance?: number;
  redirectTo?: string;
}

export const LocationSearch = (props: LocationSearchProps) => {
  const { defaultLocationList, defaultLocationOption, redirectTo, distance } =
    props;
  const { t } = useIntl();
  const router = useRouter();
  const dispatch = useDispatch();

  const [isFindLocationClicked, setIsFindLocationClicked] = useState(false);
  const [canUseDefaultLocationList, setCanUseDefaultLocationList] =
    useState(!!defaultLocationList);
  const [inputValue, setInputValue] = useState(defaultLocationOption ?? '');
  const [searchedCoordinates, setSearchedCoordinates] = useState({
    latitude: DEFAULT_LATITUDE,
    longitude: DEFAULT_LONGITUDE,
  });
  const [searchedLocationOption, setSearchedLocationOption] =
    useState<FormattedGeolocationForDropdown | null>(null);
  const [isAddressPresent, setIsAddressPresent] = useState(false);

  const {
    noSuggestionsAvailable,
    data: locationList,
    fetchLocationSuggestions: fetchFromHook,
  } = useLocationTypeahead({
    biasLatitude: searchedCoordinates.latitude,
    biasLongitude: searchedCoordinates.longitude,
  });

  const { memberDetails } = useCurrenEligibilityCheck();

  const {
    locationBasedGyms,
    fetchLocationbasedGyms,
    loading: isGymSearchInProgress,
  } = useGymsSearchByLocation('');

  const fetchedLocationBasedGyms =
    locationBasedGyms?.locationBasedFacilityOfferings;

  useDidUpdate(() => {
    setInputValue('');
    if (noSuggestionsAvailable) {
      setIsAddressPresent(false);
    }
  }, [noSuggestionsAvailable]);

  useDidUpdate(() => {
    if (
      fetchedLocationBasedGyms &&
      fetchedLocationBasedGyms.length > 0 &&
      redirectTo
    ) {
      router?.push(redirectTo);
    }
  }, [fetchedLocationBasedGyms]);

  useEffect(() => {
    if (defaultLocationOption) {
      handleAddressSelected(defaultLocationOption);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showNoGymsAvailableErrorMessage =
    !isGymSearchInProgress &&
    isFindLocationClicked &&
    (!fetchedLocationBasedGyms || fetchedLocationBasedGyms.length === 0);

  const fetchLocationSuggestions = useCallback(
    (query: string) => {
      if (query === '') return;
      setCanUseDefaultLocationList(false);
      fetchFromHook(query);
    },
    [fetchFromHook],
  );

  const handleAddressSelected = (
    addressOption: FormattedGeolocationForDropdown,
  ) => {
    if (addressOption === null) {
      return;
    }

    const { value } = addressOption;
    const latitude = value.latitude || 0;
    const longitude = value.longitude || 0;

    // If user selectes the same address, no action is needed.
    if (
      isAddressPresent &&
      latitude === searchedCoordinates.latitude &&
      longitude === searchedCoordinates.longitude
    ) {
      return;
    }
    setSearchedLocationOption(addressOption);
    setIsAddressPresent(true);
    setIsFindLocationClicked(false);
    setSearchedCoordinates({ latitude, longitude });
  };

  const handleOnFindLocationClick = (e) => {
    e.preventDefault();
    setIsFindLocationClicked(true);
    dispatch(
      eligibilityLookupSlice.actions.setSearchedLocationOption(
        searchedLocationOption,
      ),
    );
    dispatch(
      eligibilityLookupSlice.actions.setSearchedLocationList(locationList),
    );
    fetchLocationbasedGyms(
      {
        ...searchedCoordinates,
        distance: distance ?? DEFAULT_DISTANCE,
      },
      memberDetails?.sector || '',
    );
  };

  return (
    <>
      <h6 className="has-rds-mb-16 is-rds-h6 rds-font-weight-medium">
        {t('facility-web.location-search')}
      </h6>
      <div className="large-box has-rds-bg-white has-rds-p-24 has-rds-radius-4 has-rds-box-shadow-neutral-spread-6">
        <p className="is-rds-body-3 has-rds-font-weight-semibold">
          {t('facility-web.description.find-location')}
        </p>
        <form id="submit-eligibility" onSubmit={handleOnFindLocationClick}>
          <div>
            <label htmlFor="zip-code" className="is-rds-body-3">
              {t('facility-web.address-or-zipcode')}
              <span className="has-rds-text-danger">*</span>
            </label>
            <StyleAddressZipInput
              data-testid="zip-code"
              className="has-rds-radius-4 has-rds-mt-8 has-rds-mb-24"
              id="zip-code"
            >
              <LocationTypeahead
                onSelectAddress={handleAddressSelected}
                onQueryChanged={fetchLocationSuggestions}
                data={
                  canUseDefaultLocationList ? defaultLocationList : locationList
                }
                usePortal={true}
                inputValue={inputValue}
                setInputValue={setInputValue}
                debouncingTime={500} // if debouncingTime is not passed. It will take default value.
              />
            </StyleAddressZipInput>
          </div>
          {showNoGymsAvailableErrorMessage && (
            <ErrorInfo message={t('facility-web.error.find-location')} />
          )}
          <PartnerButton
            data-testid="find-location"
            disabled={!isAddressPresent}
          >
            {t('facility-web.find-location')}
          </PartnerButton>
        </form>
      </div>
    </>
  );
};
